import { FormControl, InputLabel, OutlinedInput, Stack, Typography, styled } from '@mui/material';
import { useTheme } from '@mui/system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { MobilePageContainer } from 'components/views/mobile/MobilePageContainer';
import { RoutePaths } from 'constants/RoutePaths';
import { emailRegex } from 'constants/regex';
import { useForgotPassword } from 'hooks/useForgotPassword';
import { useForm } from 'react-hook-form';

const StyledErrorDiv = styled('div')(({ theme }) => ({
  color: theme.palette.error.main,
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}));

const StyledFormStack = styled(Stack)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(4),
}));

const StyledInformationDiv = styled('div')(({ theme }) => ({
  color: theme.palette.success.main,
  textAlign: 'center',
}));

type ForgotPasswordFormValues = {
  email: string;
};

export const ForgotPasswordPageMobile = () => {
  const [emailSent, setEmailSent] = useState(false);
  const { processForgotPassword, forgotPasswordPending } = useForgotPassword();

  const { t } = useTranslation();
  const theme = useTheme();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    mode: 'onBlur',
    defaultValues: {
      email: '',
    },
  });

  const watchedEmail = watch('email');

  const handleForgotPassword = async (data: ForgotPasswordFormValues) => {
    const res = await processForgotPassword({ email: data.email });
    if (res.status === 200) {
      setEmailSent(true);
    }
  };

  return (
    <MobilePageContainer
      title={t('forgotPasswordPage.resetPassword')}
      footerText={t('forgotPasswordPage.backToLogin')}
      footerUrl={RoutePaths.LOGIN}
    >
      {emailSent ? (
        <StyledInformationDiv>{t('forgotPasswordPage.emailSent')}</StyledInformationDiv>
      ) : (
        <form onSubmit={handleSubmit(handleForgotPassword)}>
          <StyledFormStack spacing={theme.spacing(2)}>
            <Stack spacing={theme.spacing(2)}>
              <StyledFormControl variant="outlined">
                <InputLabel htmlFor="email">{t('resetPassword.emailLabel')}</InputLabel>
                <OutlinedInput
                  id="email"
                  label={t('resetPassword.emailLabel')}
                  {...register('email', {
                    required: t('forgotPasswordPage.emailRequired'),
                    pattern: {
                      value: emailRegex,
                      message: t('forgotPasswordPage.emailInvalid'),
                    },
                  })}
                />
                {errors.email && <StyledErrorDiv>{errors.email.message}</StyledErrorDiv>}
              </StyledFormControl>             
              <Typography variant="caption">
                {t('forgotPasswordPage.afterClickReset')}
              </Typography>
            </Stack>
            <LoadingButton
              disabled={!watchedEmail || !!errors.email}
              loading={forgotPasswordPending}
              variant="contained"
              color="primary"
              type="submit"
            >
              {t('forgotPasswordPage.resetPassword')}
            </LoadingButton>
          </StyledFormStack>
        </form>
      )}
    </MobilePageContainer>
  );
};
